<template>
  <v-dialog v-model="isOpen" max-width="1370px" scrollable persistent>
    <v-card>
      <v-toolbar class="primary--text" flat>
        <h2 class="font-weight-black primary--text mb-0">
          {{ dialogTitle }}
        </h2>
      </v-toolbar>
      <v-progress-linear
        v-if="loading"
        class="ma-0"
        color="primary"
        height="3"
        indeterminate
      />
      <v-card-text>
        <v-form ref="form" v-model="isFormValid" lazy-validation class="mx-2 px-2">
          <v-layout>
            <v-flex md12>
              <!-- ==================== DATOS GENERALES ==================== -->
              <h3 class="font-weight-black primary--text mb-0">
                {{ $t('viatics.generalData') }}
              </h3>
              <v-layout>
                <v-flex v-if="!isCreating">
                  <div class="subtitle-1 mt-2">{{ $t('viatics.folio') }} :</div>
                  <v-layout>
                    <v-flex class="pr-5">
                      <v-text-field
                        v-model="form.folio"
                        name="folio"
                        :rules="rules.required"
                        class="mt-2"
                        clearable
                        required
                        disabled
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.requestDate') }} :</div>
                  <v-layout>
                    <v-flex class="pr-5">
                      <v-text-field
                        v-model="form.requestDate"
                        name="requestDate"
                        :rules="rules.required"
                        class="mt-2"
                        clearable
                        required
                        disabled
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.user') }} :</div>
                  <v-text-field
                    v-model="form.requestedByName"
                    name="requestedByName"
                    :rules="rules.required"
                    class="mt-2 pr-5"
                    clearable
                    required
                    disabled
                  />
                </v-flex>
                <v-flex>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.assignedTo') }} :</div>
                  <v-layout row wrap>
                    <v-flex class="pr-0">
                      <lookup-field
                        v-model="form.assignedUserId"
                        name="assignedUserId"
                        :items="lists.users"
                        :display-name="form.assignedUserName"
                        :disabled="isReadonly"
                        input-class="mt-2 pr-0"
                        required
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-layout>
                <v-flex class="mr-5" md5>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.travelPeriod') }} :</div>
                  <v-layout>
                    <v-flex class="pr-5">
                      <date-field
                        v-model="form.startDate"
                        name="startDate"
                        :label="$t('viatics.from')"
                        :max="form.endDate"
                        :disabled="isReadonly"
                        required
                      />
                    </v-flex>
                    <v-flex class="pr-5">
                      <date-field
                        v-model="form.endDate"
                        name="endDate"
                        :label="$t('viatics.to')"
                        :min="form.startDate"
                        :disabled="isReadonly"
                        required
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex class="mr-4" md4>
                  <div class="subtitle-1 mt-2">{{ $t('general.project') }} :</div>
                  <v-layout>
                    <v-flex class="pr-0">
                      <lookup-field
                        v-model="form.projectId"
                        name="projectId"
                        :items="lists.projects"
                        :display-name="form.projectName"
                        :disabled="isReadonly"
                        input-class="pt-2 mt-1"
                        required
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex class="mr-4" md4>
                  <div class="subtitle-1 mt-2">{{ $t('general.orderNumber') }} :</div>
                  <v-layout>
                    <v-flex class="pr-0">
                      <v-text-field
                        v-model="form.orderNumber"
                        name="orderNumber"
                        :disabled="isReadonly"
                        class="pt-2 mt-0"
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>

              <div class="subtitle-1 pb-0 mb-0">{{ $t('general.description') }} :</div>
              <v-layout>
                <v-flex md12>
                  <v-textarea
                    v-model="form.description"
                    name="description"
                    :disabled="isReadonly"
                    class="pt-0 mt-0"
                    rows="1"
                    auto-grow
                  />
                </v-flex>
              </v-layout>

              <!-- ==================== VIAJE ==================== -->
              <v-layout class="pr-5">
                <v-flex class="pa-0" md11>
                  <v-checkbox
                    v-model="form.isTravel"
                    name="isTravel"
                    :label="$t('viatics.travelQuestion')"
                    :disabled="isReadonly"
                    class="mb-0 pb-0"
                  />
                </v-flex>
              </v-layout>

              <v-layout v-if="form.isTravel">
                <v-flex md4>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.travelType') }} :</div>
                </v-flex>
                <v-flex md4>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.origin') }} :</div>
                </v-flex>
                <v-flex md4>
                  <div class="subtitle-1 mt-2">{{ $t('viatics.destination') }} :</div>
                </v-flex>
              </v-layout>
              <v-layout v-if="form.isTravel">
                <v-flex md4>
                  <lookup-field
                    v-model="form.travelTypeId"
                    name="travelTypeId"
                    :items="lists.travelTypes"
                    :display-name="form.travelTypeName"
                    :disabled="isReadonly"
                    input-class="pr-5 mt-0 pt-0 ajuste"
                  />
                </v-flex>
                <v-flex md4>
                  <v-text-field
                    v-model="form.origin"
                    name="origin"
                    :disabled="isReadonly"
                    class="pr-5 mt-0 pt-0"
                    clearable
                  />
                </v-flex>
                <v-flex md4>
                  <v-text-field
                    v-model="form.destination"
                    name="destination"
                    :disabled="isReadonly"
                    class="mt-0 pt-0"
                    clearable
                  />
                </v-flex>
              </v-layout>

              <!-- ==================== VEHÍCULO ==================== -->
              <v-layout>
                <v-flex class="pa-0" md11>
                  <v-checkbox
                    v-model="form.isVehicle"
                    name="isVehicle"
                    :label="$t('viatics.vehicleQuestion')"
                    :disabled="isReadonly"
                    class="mb-0 pb-0"
                  />
                </v-flex>
              </v-layout>
              <v-layout v-if="form.isVehicle" wrap>
                <v-flex md3>
                  <lookup-field
                    v-model="form.vehicle.vehicleId"
                    name="vehicleId"
                    :items="lists.vehicles"
                    :display-name="vehicleName"
                    :label="$t('general.vehicle')"
                    :disabled="isReadonly"
                    input-class="pr-5 mt-0 pt-0 ajuste"
                  />
                </v-flex>
                <v-flex md3>
                  <v-text-field
                    :value="licensePlate"
                    :label="$t('general.licensePlate')"
                    class="pr-5 mt-0 pt-1"
                    disabled
                  />
                </v-flex>
                <v-flex md3>
                  <v-text-field
                    v-model="form.vehicle.initialMileage"
                    name="vehicleInitialMileage"
                    :label="$t('general.initialMileage')"
                    :disabled="isReadonly"
                    class="pr-5 mt-0 pt-0 ajuste"
                    type="number"
                    step="1"
                  />
                </v-flex>
                <v-flex md3>
                  <v-text-field
                    v-model="form.vehicle.finalMileage"
                    name="vehicleFinalMileage"
                    :label="$t('general.finalMileage')"
                    :disabled="isReadonly"
                    class="pr-5 mt-0 pt-0 ajuste"
                    type="number"
                    step="1"
                  />
                </v-flex>
                <v-flex md3>
                  <v-text-field
                    v-model="form.vehicle.initialLiters"
                    name="vehicleInitialLiters"
                    :label="$t('general.litersOfGasoline')"
                    :disabled="isReadonly"
                    class="pr-5 mt-0 pt-0 ajuste"
                    type="number"
                    step="1"
                  />
                </v-flex>
                <v-flex md3>
                  <v-text-field
                    v-model="form.vehicle.finalLiters"
                    name="vehicleFinalLiters"
                    :label="$t('general.finalLitersOfGasoline')"
                    :disabled="isReadonly"
                    class="pr-5 mt-0 pt-0 ajuste"
                    type="number"
                    step="1"
                  />
                </v-flex>
              </v-layout>
              <v-layout v-if="form.isVehicle">
                <v-flex md12>
                  <v-textarea
                    v-model="form.vehicle.notes"
                    name="vehicleNotes"
                    :disabled="isReadonly"
                    :label="$t('general.description')"
                    rows="2"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>

          <!-- ==================== CATEGORÍAS ==================== -->
          <v-layout>
            <v-flex md12 class="mt-2">
              <h3 class="font-weight-black primary--text mb-0">
                {{ $t('general.categories') }}
              </h3>
              <v-layout>
                <v-flex class="text-right">
                  <v-btn color="primary" :disabled="isReadonly" @click="addCategory">
                    {{ $t('viatics.addCategorie') }}
                  </v-btn>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex xs12>
                  <request-categories-table
                    :categories="form.categories"
                    :catalog="lists.categories"
                    :readonly="isReadonly"
                    :min-date="form.startDate"
                    :max-date="form.endDate"
                    @remove="removeCategory"
                  />
                </v-flex>
              </v-layout>

              <!-- ==================== AMPLIACIONES ==================== -->
              <section
                v-for="(extension, index) in form.extensions"
                :key="extension.id || index"
                :class="extensionClass(extension)"
              >
                <v-layout class="d-flex justify-end">
                  <v-flex
                    md2
                    class="mr-3 text-center d-flex justify-content-center align-items-center"
                  >
                    <div class="text-container">
                      <b><span>{{ extensionLabel(index) }}</span></b>
                      <b
                        v-if="isRejected(extension.status)"
                        class="font-weight-black"
                        style="color:red"
                      >
                        {{ $t('expenses.status.rejected') }}
                      </b>
                    </div>
                  </v-flex>
                  <v-flex v-if="extension.folio" md2 class="mr-3">
                    <v-text-field
                      :value="extension.folio"
                      :label="$t('general.folio')"
                      hide-details
                      readonly
                      disabled
                    />
                  </v-flex>
                  <v-flex v-if="extension.status" md2 class="mr-3">
                    <v-text-field
                      :value="statusText(extension.status)"
                      :label="$t('general.status')"
                      hide-details
                      readonly
                      disabled
                    />
                  </v-flex>
                  <v-flex md3 class="mr-3">
                    <v-text-field
                      :value="extension.startDate"
                      :label="$t('general.startDate')"
                      hide-details
                      readonly
                      disabled
                    />
                  </v-flex>
                  <v-flex md3>
                    <v-text-field
                      :value="extension.endDate"
                      :label="$t('general.selectEndDate')"
                      hide-details
                      readonly
                      disabled
                    />
                  </v-flex>
                </v-layout>

                <v-layout>
                  <v-flex xs12>
                    <request-categories-table
                      :categories="extension.categories"
                      :catalog="lists.categories"
                      :total-label="extensionTotalLabel(index)"
                      :row-class="extensionClass(extension)"
                      :table-class="extensionClass(extension)"
                      readonly
                    />
                  </v-flex>
                </v-layout>

                <v-layout class="d-flex justify-end">
                  <v-flex md9>
                    <v-checkbox
                      :input-value="extension.hasAdvance"
                      :label="$t('viatics.advanceQuestion')"
                      disabled
                    />
                  </v-flex>
                  <v-flex md3>
                    <v-text-field
                      v-if="extension.hasAdvance"
                      :value="extension.advanceAmount"
                      class="ml-3"
                      type="number"
                      min="0"
                      disabled
                    />
                  </v-flex>
                </v-layout>
              </section>

              <br />

              <!-- ==================== ORIGEN DEL RECURSO ==================== -->
              <v-layout>
                <v-flex md4>
                  <v-radio-group
                    v-model="form.fundingType"
                    :disabled="isReadonly"
                    name="fundingType"
                  >
                    <v-radio label="Caja chica / Tarjeta AMEX" :value="fundingTypes.PETTY_CASH" />
                    <v-radio label="Anticipo" :value="fundingTypes.ADVANCE" />
                    <v-radio label="Reembolso" :value="fundingTypes.REFUND" />
                  </v-radio-group>
                </v-flex>
                <v-flex md4>
                  <!-- Espacio del cargador de archivos; lo inyecta la vista anfitriona. -->
                  <slot name="uploader"></slot>
                </v-flex>
                <v-flex md4>
                  <v-text-field
                    v-model="form.totalAmount"
                    name="totalAmount"
                    :rules="rules.required"
                    :disabled="isReadonly"
                    class="ml-3"
                    width="30px"
                    prepend-icon="attach_money"
                    type="number"
                    min="0"
                    clearable
                    required
                  />
                </v-flex>
              </v-layout>

              <!-- ==================== ARCHIVOS ==================== -->
              <v-layout wrap>
                <v-flex xs12>
                  <h3 class="primary--text font-weight-black">{{ $t('expenses.files') }}:</h3>
                </v-flex>
                <v-flex v-if="form.attachments.length > 0">
                  <v-btn
                    v-for="attachment in form.attachments"
                    :key="attachment.id"
                    color="primary"
                    dark
                    @click="$emit('view-file', attachment)"
                  >
                    <v-icon left>{{ attachmentIcon(attachment) }}</v-icon>
                    <h3 class="pb-0 mb-0">{{ $t('expenses.file') }} {{ attachment.fileId }}</h3>
                  </v-btn>
                </v-flex>
                <v-flex v-else-if="form.id">
                  <v-alert color="info" outline :value="true" icon="info">
                    {{ $t('expenses.noFiles') }}
                  </v-alert>
                </v-flex>
              </v-layout>

              <!-- ==================== MONTO AUTORIZADO ==================== -->
              <div v-if="showAuthorizedAmount" class="subtitle-1 mt-2">
                {{ $t('viatics.amountAuthorized') }}
              </div>
              <v-layout v-if="showAuthorizedAmount">
                <v-text-field
                  :value="form.authorizedAmount"
                  class="ml-3"
                  width="30px"
                  prepend-icon="attach_money"
                  min="0"
                  clearable
                  required
                  disabled
                />
              </v-layout>

              <!-- ==================== VALIDADOR ==================== -->
              <div class="subtitle-1 mt-2">{{ $t('viatics.validators') }} :</div>
              <v-layout>
                <v-flex md11>
                  <lookup-field
                    v-model="form.validatorId"
                    name="validatorId"
                    :items="lists.validators"
                    :display-name="form.validatorName"
                    :disabled="isReadonly || isApproved"
                    required
                  />
                </v-flex>
              </v-layout>

              <!-- ==================== MOTIVO DE RECHAZO ==================== -->
              <div v-if="showRejectedComment" class="subtitle-1 mt-2">
                {{ $t('viatics.reasonRejected') }}:
              </div>
              <v-layout v-if="showRejectedComment">
                <v-flex
                  xs12
                  class="pa-2 mt-3"
                  style="background-color: #f7665e; border-radius: 5px"
                >
                  <v-icon small class="mb-1" color="#FFF">fas fa-thumbs-down</v-icon>
                  <p><strong>{{ form.validationComment }}</strong></p>
                </v-flex>
              </v-layout>

              <!-- ==================== ANTICIPO TOTAL ==================== -->
              <v-layout v-if="showTotalAdvance" class="d-flex justify-start my-4">
                <v-flex md9>
                  <span class="h6 font-weight">{{ $t('viatics.totalAmountAdvance') }}:</span>
                  <span class="h6"> {{ totalAdvance | formatCurrency }} MXN </span>
                </v-flex>
                <v-flex md3></v-flex>
              </v-layout>

              <!-- ==================== MENSAJE DE VALIDACIÓN ==================== -->
              <div v-if="showApprovedComment" class="subtitle-1 mt-2">
                {{ $t('viatics.validationMessage') }}:
              </div>
              <v-layout v-if="showApprovedComment">
                <v-flex
                  xs12
                  class="pa-2 mt-3"
                  style="background-color: #73d673; border-radius: 5px"
                >
                  <v-icon small class="mb-1" color="#FFF">fas fa-thumbs-up</v-icon>
                  <p><strong>{{ form.validationComment }}</strong></p>
                </v-flex>
              </v-layout>

              <!-- ==================== DESCARGAS ==================== -->
              <v-layout v-if="!isCreating" class="mt-5">
                <v-flex>
                  {{ $t('viatics.downloadRequest') }}
                  <v-btn
                    :loading="downloading === 'pdf'"
                    class="mr-0"
                    icon
                    flat
                    color="error"
                    @click="$emit('download', { type: 'pdf', request: form })"
                  >
                    <v-icon dark>mdi-file-pdf-box</v-icon>
                  </v-btn>
                  <v-btn
                    :loading="downloading === 'xls'"
                    class="ml-0"
                    icon
                    flat
                    color="green"
                    @click="$emit('download', { type: 'xls', request: form })"
                  >
                    <v-icon dark>mdi-file-excel-box</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary darken-1" round flat @click="close">
          {{ closeLabel }}
        </v-btn>
        <v-btn
          v-if="!isReadonly"
          :loading="saving"
          :disabled="saving"
          color="primary"
          class="white--text"
          round
          depressed
          @click="submit"
        >
          {{ isCreating ? $t('general.create') : $t('general.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DateField from './DateField.vue'
import LookupField from './LookupField.vue'
import RequestCategoriesTable from './RequestCategoriesTable.vue'
import {
  FILE_TYPE,
  FUNDING_TYPE,
  REQUEST_STATUS,
  buildRequestModel,
  calculateTotalAdvance,
  emptyRequestModel,
  isCompressedFile
} from './request.model'

const STATUS_LABEL_KEYS = {
  [REQUEST_STATUS.CREATED]: 'general.requestStatus.created',
  [REQUEST_STATUS.SENT]: 'general.requestStatus.sent',
  [REQUEST_STATUS.IN_REVIEW]: 'general.requestStatus.in_review',
  [REQUEST_STATUS.APPROVED]: 'general.requestStatus.approved',
  [REQUEST_STATUS.REJECTED]: 'general.requestStatus.rejected'
}

const EMPTY_CATALOGS = {
  users: [],
  projects: [],
  travelTypes: [],
  vehicles: [],
  categories: [],
  validators: []
}

/**
 * Detalle de una solicitud de viáticos / anticipo.
 *
 * Se usa con v-model sobre la visibilidad y recibe la solicitud "cruda" tal como
 * la entrega el back (cualquiera de sus dos formas). El componente no consulta
 * servicios: todo lo que necesita llega por props, y todo lo que produce sale
 * por eventos, de modo que la misma pieza sirve en cualquier pantalla.
 *
 * Estado:
 *  - `readonly` (por defecto, y lo único que se usa hoy) → todo deshabilitado.
 *  - `readonly=false` → inputs activos; al guardar emite `submit` con el modelo.
 *    Se considera alta cuando la solicitud no trae folio ni id.
 */
export default {
  name: 'RequestDetailDialog',
  components: { DateField, LookupField, RequestCategoriesTable },
  props: {
    /** Visibilidad del diálogo (v-model). */
    value: {
      type: Boolean,
      default: false
    },
    /** Solicitud cruda; puede llegar en null o incompleta. */
    request: {
      type: Object,
      default: null
    },
    /**
     * Bandera única de solo lectura. Hoy todas las vistas la dejan en true;
     * ponerla en false habilita la edición/alta sin tocar el componente.
     */
    readonly: {
      type: Boolean,
      default: true
    },
    /** Muestra la barra de progreso mientras el endpoint responde. */
    loading: {
      type: Boolean,
      default: false
    },
    /** Catálogos opcionales; sin ellos el modal sigue funcionando en solo lectura. */
    catalogs: {
      type: Object,
      default: () => ({})
    },
    saving: {
      type: Boolean,
      default: false
    },
    /** 'pdf' | 'xls' mientras se genera la descarga. */
    downloading: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      form: emptyRequestModel(),
      isFormValid: true,
      fundingTypes: FUNDING_TYPE
    }
  },
  computed: {
    isOpen: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    rules () {
      return this.$validationRules()
    },
    isReadonly () {
      return this.readonly
    },
    /** Alta: todavía no existe del lado del back (sin folio ni id). */
    isCreating () {
      return !this.form.id && !this.form.folio
    },
    isApproved () {
      return this.form.status === REQUEST_STATUS.APPROVED
    },
    dialogTitle () {
      if (this.title) return this.title
      if (this.isCreating) return this.$t('viatics.new')
      if (this.isReadonly) return this.$t('viatics.requestDetails')
      return this.$t('viatics.edit')
    },
    closeLabel () {
      const status = this.form.status
      if (!status || status === REQUEST_STATUS.IN_REVIEW) return this.$t('general.cancel')
      return this.$t('general.close')
    },
    /** Catálogos siempre como arreglo, sin mutar la prop recibida. */
    lists () {
      const source = this.catalogs || {}
      const lists = {}
      Object.keys(EMPTY_CATALOGS).forEach(key => {
        lists[key] = Array.isArray(source[key]) ? source[key] : EMPTY_CATALOGS[key]
      })
      return lists
    },
    totalAdvance () {
      return calculateTotalAdvance(this.form)
    },
    showAuthorizedAmount () {
      return this.isApproved && this.form.authorizedAmount > 0
    },
    showTotalAdvance () {
      return this.form.status === REQUEST_STATUS.IN_REVIEW || this.isApproved
    },
    showRejectedComment () {
      return this.form.status === REQUEST_STATUS.REJECTED && !!this.form.validationComment
    },
    showApprovedComment () {
      return this.isApproved && !!this.form.validationComment
    },
    vehicleName () {
      const match = this.lists.vehicles.find(
        vehicle => vehicle && vehicle.id === this.form.vehicle.vehicleId
      )
      return match ? match.name : ''
    },
    licensePlate () {
      if (this.form.vehicle.licensePlate) return this.form.vehicle.licensePlate

      const match = this.lists.vehicles.find(
        vehicle => vehicle && vehicle.id === this.form.vehicle.vehicleId
      )
      return match && match.license_plate
        ? match.license_plate
        : 'No ha seleccionado un vehículo'
    }
  },
  watch: {
    request: {
      immediate: true,
      handler () {
        this.syncForm()
      }
    },
    value (isOpen) {
      if (isOpen) this.syncForm()
    }
  },
  methods: {
    syncForm () {
      this.form = buildRequestModel(this.request)
      if (this.$refs.form && typeof this.$refs.form.resetValidation === 'function') {
        this.$refs.form.resetValidation()
      }
    },
    statusText (status) {
      if (!status) return ''
      const key = STATUS_LABEL_KEYS[status]
      // El reporte puede enviar el estatus ya traducido; en ese caso se respeta.
      return key ? this.$t(key) : status
    },
    isRejected (status) {
      return status === REQUEST_STATUS.REJECTED
    },
    extensionClass (extension) {
      return this.isRejected(extension.status) ? 'custom-bg' : ''
    },
    extensionLabel (index) {
      return `${this.$t('viatics.extension')} - No.${index + 1}`
    },
    extensionTotalLabel (index) {
      return `${this.$t('general.total')} ${this.extensionLabel(index)}`
    },
    attachmentIcon (attachment) {
      if (isCompressedFile(attachment.fileTypeId)) return 'fas fa-file-archive'
      if (attachment.fileTypeId === FILE_TYPE.PDF) return 'description'
      return 'insert_photo'
    },
    addCategory () {
      this.form.categories.push({
        id: null,
        categoryId: null,
        name: '',
        price: 0,
        quantity: 0,
        peopleCount: 0,
        notes: '',
        startDate: '',
        endDate: '',
        amount: 0
      })
    },
    removeCategory (index) {
      this.form.categories.splice(index, 1)
    },
    close () {
      this.isOpen = false
      this.$emit('close')
    },
    submit () {
      if (this.$refs.form && !this.$refs.form.validate()) return
      this.$emit('submit', this.form)
    }
  }
}
</script>

<style scoped>
.custom-bg {
  background-color: rgba(221, 27, 27, 0.267);
}
.text-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/requests/RequestDetailDialog.vue